import { useMemo, useCallback } from "react";
import * as Sentry from "@sentry/react";

import { DateTime } from "luxon";

import type { SupportedLanguage } from "../i18n/i18n";
import { getAgeFromDateOfBirth } from "./getAgeFromDateOfBirth";

import { useIntakeFormStore, type IntakeFormState } from "../screens/IntakeFormScreen/intakeFormState";
import type { CreatePhiFileReferenceMutationVariables } from "../__generated__/mcs_backend_v1/graphql";

type BuildTypeformUrlProps = {
  memberId: string | null | undefined;
  currentLanguage: SupportedLanguage;
  consentToTreat: boolean;
  caregiverInfo: {
    email: string;
  };
  caregiverAddress: {
    provinceCode: string;
  };
  memberInfo: {
    firstName: string;
    gender: string;
    race: string;
    ethnicity: string;
    dateOfBirth: string;
  };
  insuranceProviderSlug: string | undefined;
};

// Note: If the slugs in InsuranceProvider table change, these constants will need to be updated
const BCBS_COMMERCIAL_PROVIDER_LABEL_PREFIX = "BCBS_COMMERCIAL";
const MOLINA_PROVIDER_LABEL_PREFIX = "MOLINA";

const TYPEFORM_URLS: Record<string, Record<SupportedLanguage, string>> = {
  // https://neuromotion.slack.com/archives/C014Y8MU0DP/p1724164930554839
  MOLINA: {
    en: "https://mightierhealth.typeform.com/to/Z1yX53Y1",
    es: "https://mightierhealth.typeform.com/to/wmLsfVjI",
    ar: "https://mightierhealth.typeform.com/to/fc2uKjiE",
    ru: "https://mightierhealth.typeform.com/to/qzQzVqE8",
    uk: "https://mightierhealth.typeform.com/to/lp8rb7VO",
    vi: "https://mightierhealth.typeform.com/to/wd7wOAKF",
    "zh-CN": "https://mightierhealth.typeform.com/to/VID3akJa",
    "fr-CA": "https://mightierhealth.typeform.com/to/vCurBpUD",
    so: "https://mightierhealth.typeform.com/to/mLnPgKg2",
    hmn: "https://mightierhealth.typeform.com/to/zfSYEhjc",
  },
  BCBS_COMMERCIAL: {
    // https://neuromotion.slack.com/archives/C014Y8MU0DP/p1753457305000489
    en: "https://mightierhealth.typeform.com/to/EpnLTr8M",
    es: "https://mightierhealth.typeform.com/to/w4Gulaqp",
    ar: "https://mightierhealth.typeform.com/to/UNwOY6k7",
    ru: "https://mightierhealth.typeform.com/to/zSysWgiy",
    uk: "https://mightierhealth.typeform.com/to/idzVZVHZ",
    vi: "https://mightierhealth.typeform.com/to/LWQJ33fR",
    "zh-CN": "https://mightierhealth.typeform.com/to/NzfRhwsN",
    "fr-CA": "https://mightierhealth.typeform.com/to/lL76r2RD",
    so: "https://mightierhealth.typeform.com/to/sJ86qB2T",
    hmn: "https://mightierhealth.typeform.com/to/s1sgoW1G",
  },
};

const getTypeformBaseUrl = (
  currentLanguage: SupportedLanguage,
  insuranceProviderSlug: string | undefined,
): string | undefined => {
  // Default Typeform is BCBS
  let insuranceProviderSlugPrefix = BCBS_COMMERCIAL_PROVIDER_LABEL_PREFIX;

  if (insuranceProviderSlug?.includes(MOLINA_PROVIDER_LABEL_PREFIX)) {
    insuranceProviderSlugPrefix = MOLINA_PROVIDER_LABEL_PREFIX;
  }

  return TYPEFORM_URLS[insuranceProviderSlugPrefix]?.[currentLanguage];
};

export const getMemberDatabasePk = (memberId: string): string | undefined => {
  const decodedId = atob(memberId);
  const memberDatabasePk = decodedId.split("MemberNode:")[1];

  if (!memberDatabasePk) {
    Sentry.captureMessage("Unable to parse memberId to include in typeform hidden fields", {
      extra: { memberId },
    });
    return;
  }
  return memberDatabasePk;
};

export const buildTypeformUrl = ({
  memberId,
  currentLanguage,
  consentToTreat,
  caregiverInfo,
  caregiverAddress,
  memberInfo,
  insuranceProviderSlug,
}: BuildTypeformUrlProps): string | null => {
  // Depending on type of insurance provider, different URL for Typeform is used
  const typeformBaseUrl = getTypeformBaseUrl(currentLanguage, insuranceProviderSlug);
  if (!typeformBaseUrl) {
    Sentry.captureMessage("Attempted to submit intake form with invalid locale", {
      extra: { currentLanguage },
    });
    return null;
  }
  const paramsObj: Record<string, string> = {
    has_consented: consentToTreat ? "true" : "false",
    email: caregiverInfo.email,
    caregiver_locale: currentLanguage,
    member_state: caregiverAddress.provinceCode,
    member_first_name: memberInfo.firstName,
    gender: memberInfo.gender,
    race: memberInfo.race,
    ethnicity: memberInfo.ethnicity,
    member_age: getAgeFromDateOfBirth(memberInfo.dateOfBirth),
  };

  // Include member_id only if it exists (ex: this method called from useSubmitMutation when memberId has been set)
  if (memberId) {
    paramsObj.member_id = memberId;
  }

  if (insuranceProviderSlug) {
    paramsObj.insurance_provider_slug = insuranceProviderSlug;
  }

  const params = new URLSearchParams(paramsObj);

  return `${typeformBaseUrl}#${params.toString()}`;
};

/**
 *
 */
interface IntakePhiFileIdentifyingInfo {
  member: Partial<Pick<NonNullable<IntakeFormState["memberInfo"]>, "firstName" | "lastName" | "dateOfBirth">>;
  caregiver: Partial<Pick<NonNullable<IntakeFormState["caregiverInfo"]>, "firstName" | "lastName">>;
}

// https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html

const S3_PREFIX = "intake-insurance-uploads";
const S3_KEY_UNSAFE_CHARS = /[^a-zA-Z0-9!\-_.*'()]/g;
const S3_MAX_KEY_LENGTH = 1024;

type BuildIntakePhiFileInputFn = (
  slug: string,
) => Pick<CreatePhiFileReferenceMutationVariables["input"], "s3Key" | "identifyingInfo">;

export const useBuildIntakePhiFileInputFn = (): { buildIntakePhiFileInput: BuildIntakePhiFileInputFn } => {
  const { memberInfo, caregiverInfo } = useIntakeFormStore();

  const identifyingInfo = useMemo<IntakePhiFileIdentifyingInfo>(() => {
    return {
      member: {
        firstName: memberInfo?.firstName,
        lastName: memberInfo?.lastName,
        dateOfBirth: memberInfo?.dateOfBirth,
      },
      caregiver: {
        firstName: caregiverInfo?.firstName,
        lastName: caregiverInfo?.lastName,
      },
    };
  }, [memberInfo, caregiverInfo]);

  const buildIntakePhiFileInput = useCallback(
    (slug: string) => {
      // 2025-06-24T15:43:12
      const timestamp = DateTime.now().toFormat("yyyy-MM-ddTHH:mm:ss");

      const allValues = [...Object.values(identifyingInfo.member), "-", ...Object.values(identifyingInfo.caregiver)];

      const lengthOfConstants = S3_PREFIX.length + timestamp.length + slug.length;
      const maxLengthPerKey = Math.floor((S3_MAX_KEY_LENGTH - lengthOfConstants) / allValues.length);

      const formattedVals = allValues
        .map((val) => val.slice(0, maxLengthPerKey))
        .join("-")
        .replace(S3_KEY_UNSAFE_CHARS, "*");

      // example full S3 key
      // intake-insurance-uploads/memberFirst-memberLast-2020-05-23---parentFirst-parentLast-2025-07-30T12:00:12-ins-card-front
      const allKeyParts = [formattedVals, timestamp, slug];
      const s3Key = `${S3_PREFIX}/${allKeyParts.join("-")}`;

      return {
        identifyingInfo: JSON.stringify(identifyingInfo),
        s3Key,
      };
    },
    [identifyingInfo],
  );

  return useMemo(() => ({ buildIntakePhiFileInput }), [buildIntakePhiFileInput]);
};
